
import {
  Pin, History
} from 'lucide-react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslations } from '../i18n';
import { theme, styles } from '../lib/theme';
import { Car } from '../lib/supabase';
import { NoteItem } from '../components/cars/NoteItem';


interface NotesListProps {
  car: Car;
  notes: any[];
  loading?: boolean;
  isPublic?: boolean;
  highlightId?: string | null;
}

export function NotesList({ car, notes, loading, highlightId }: NotesListProps) {
  const { slug } = useParams();
  const outletContext = useOutletContext<any>() || {};
  const { t } = useTranslations();
  const isAdminView = outletContext.isAdminView || false;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 font-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500 font-semibold italic">{t.common.loading}...</p>
      </div>
    );
  }

  return (
    <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
      {/* Notes List */}
      <div className="space-y-4">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 font-primary">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
            <p className="text-gray-500 font-semibold italic">{t.common.loading}...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className={`${styles.card} p-12 text-center shadow-sm`}>
            <History size={48} className="text-gray-200 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">{t.notes.noNotes}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pinned Section */}
            {notes.some(n => n.is_pinned) && (
              <div className="space-y-3">
                {notes.some(n => !n.is_pinned) && (
                  <h4 className={`${theme.typography.sectionTitle} px-1 flex items-center gap-2`}>
                    <Pin size={16} className="fill-current text-blue-600 dark:text-blue-400" />
                    {t.notes.pinToTop || 'Pinned'}
                  </h4>
                )}
                <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
                  {notes.filter(n => n.is_pinned).map(note => (
                    <NoteItem key={note.id} note={note} slug={slug} isAdminView={isAdminView} carIsPublic={car.is_public} highlighted={highlightId === note.id} />
                  ))}
                </div>
              </div>
            )}

            {/* Other Notes Section */}
            {(notes.some(n => !n.is_pinned) || !notes.some(n => n.is_pinned)) && (
              <div className="space-y-3">
                {notes.some(n => n.is_pinned) && (
                  <h4 className={`${theme.typography.sectionTitle} px-1`}>
                    {t.notes.title}
                  </h4>
                )}
                <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
                  {notes.filter(n => !n.is_pinned).map(note => (
                    <NoteItem key={note.id} note={note} slug={slug} isAdminView={isAdminView} carIsPublic={car.is_public} highlighted={highlightId === note.id} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
